"use client";

import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import remarkGfm from "remark-gfm";
import rehypeKatex from "rehype-katex";

/**
 * One renderer for every Maren-generated message. Handles:
 *  - Full markdown (headings, bold, italic, lists, code, tables via GFM)
 *  - Inline math:  $e = mc^2$
 *  - Display math: $$\rho = \frac{1}{\sigma}$$
 *
 * Global KaTeX CSS is imported once in app/layout.tsx.
 *
 * Tailwind's prose plugin isn't installed, so we style the output with
 * per-element className overrides. Keep the palette in sync with the
 * chat bubbles (charcoal on maren-green backgrounds).
 */
export default function MarkdownMessage({ text }: { text: string }) {
  return (
    <div className="markdown-body text-sm leading-relaxed">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex]}
        components={{
          p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
          h1: ({ children }) => <h3 className="text-base font-bold mt-3 mb-1.5">{children}</h3>,
          h2: ({ children }) => <h3 className="text-sm font-bold mt-3 mb-1.5">{children}</h3>,
          h3: ({ children }) => <h3 className="text-sm font-bold mt-3 mb-1.5">{children}</h3>,
          strong: ({ children }) => <strong className="font-bold">{children}</strong>,
          em: ({ children }) => <em className="italic">{children}</em>,
          ul: ({ children }) => <ul className="list-disc list-outside ml-5 mb-2 space-y-0.5">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal list-outside ml-5 mb-2 space-y-0.5">{children}</ol>,
          li: ({ children }) => <li className="pl-0.5">{children}</li>,
          hr: () => <hr className="my-3 border-current opacity-20" />,
          code: ({ children, className }) => {
            const isBlock = className?.includes("language-");
            if (isBlock) {
              return (
                <pre className="my-2 rounded-md bg-maren-charcoal/50 dark:bg-maren-charcoal/70 p-3 overflow-x-auto">
                  <code className="text-xs font-mono text-maren-stone">{children}</code>
                </pre>
              );
            }
            return (
              <code className="rounded bg-maren-sage/25 px-1 py-0.5 text-[0.85em] font-mono">
                {children}
              </code>
            );
          },
          blockquote: ({ children }) => (
            <blockquote className="border-l-2 border-current opacity-70 pl-3 my-2 italic">
              {children}
            </blockquote>
          ),
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 hover:opacity-80"
            >
              {children}
            </a>
          ),
          table: ({ children }) => (
            <div className="overflow-x-auto my-2">
              <table className="text-xs border-collapse">{children}</table>
            </div>
          ),
          th: ({ children }) => (
            <th className="border border-current/30 px-2 py-1 font-semibold text-left">{children}</th>
          ),
          td: ({ children }) => (
            <td className="border border-current/20 px-2 py-1">{children}</td>
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}
